/** Starea wizardului de rezervare: un singur loc pentru date, calcule si salvarea locala.
 *
 *  Regula de bani e cea din `lib/money.ts` (prag 25 lei, ambele monede, curs selectabil);
 *  aici se adauga doar aritmetica fiscala a rezervarii: marja, TVA pe regim, totalul.
 */
export type Turist = {
  full_name: string
  birth_date?: string | null
  cnp?: string | null
  id_document_type?: string | null
  id_series?: string | null
  id_number?: string | null
  phone?: string | null
  email?: string | null
  is_titular?: boolean
}

export type Camera = {
  room_type: string
  meal_type: string
  room_view?: string | null
  room_notes?: string | null
  tourists: Turist[]
}

export type ServiciuExtra = {
  service_type: string
  description: string
  supplier_name?: string | null
  supplier_ref?: string | null
  begin_date?: string | null
  end_date?: string | null
  sale_price: string
  cost_price: string
  currency: string
}

export type GrupFacturare = {
  name: string
  partner_name: string
  partner_vat_no?: string | null
  invoice_amount: string
  currency: string
  room_indices: number[]
}

export type StareWizard = {
  pas: number
  // pasul 1
  service_type: string
  destination: string
  hotel: string
  supplier_name: string
  supplier_id: number | null
  supplier_ref: string
  check_in: string
  check_out: string
  adults: string
  children: string
  infants: string
  num_rooms: string
  currency: string
  cost_price: string
  sale_price: string
  destination_region: string
  vat_type: string
  exchange_rate: string
  // pasul 2
  rooms: Camera[]
  // pasul 3
  extra_services: ServiciuExtra[]
  // pasul 4
  titular_type: 'PF' | 'PJ'
  titular_name: string
  titular_cnp: string
  titular_cui: string
  titular_phone: string
  titular_email: string
  titular_address: string
  billing_groups: GrupFacturare[]
  // pasul 6
  remarks: string
  generate_bon_comanda: boolean
}

export const CAMERE = [
  { v: 'SGL', t: 'Single' }, { v: 'DBL', t: 'Dubla' }, { v: 'TWIN', t: 'Twin' },
  { v: 'TPL', t: 'Tripla' }, { v: 'QUAD', t: 'Quadrupla' }, { v: 'FAM', t: 'Familiala' },
  { v: 'SUITE', t: 'Suita' }, { v: 'APT', t: 'Apartament' },
]

export const MESE = [
  { v: 'RO', t: 'Fara masa' }, { v: 'BB', t: 'Mic dejun' }, { v: 'HB', t: 'Demipensiune' },
  { v: 'FB', t: 'Pensiune completa' }, { v: 'AI', t: 'All inclusive' },
  { v: 'UAI', t: 'Ultra all inclusive' },
]

export const REGIUNI = [
  { v: 'RO', t: 'Romania', tvaImplicit: 'A.311' },
  { v: 'UE', t: 'Uniunea Europeana', tvaImplicit: 'TI' },
  { v: 'NON_UE', t: 'Extra-UE', tvaImplicit: 'NIR' },
]

const peste = (zile: number) => new Date(Date.now() + zile * 86_400_000).toISOString().slice(0, 10)

export const stareInitiala = (): StareWizard => ({
  pas: 1,
  service_type: 'SEJUR_CHARTER', destination: '', hotel: '',
  supplier_name: '', supplier_id: null, supplier_ref: '',
  check_in: peste(30), check_out: peste(37),
  adults: '2', children: '0', infants: '0', num_rooms: '1',
  currency: 'EUR', cost_price: '', sale_price: '',
  destination_region: 'UE', vat_type: 'TI', exchange_rate: '',
  rooms: [],
  extra_services: [],
  titular_type: 'PF', titular_name: '', titular_cnp: '', titular_cui: '',
  titular_phone: '', titular_email: '', titular_address: '',
  billing_groups: [],
  remarks: '', generate_bon_comanda: true,
})

export const nr = (v: string | number | null | undefined) =>
  Number(String(v ?? '').replace(',', '.')) || 0

export const nopti = (s: StareWizard) => {
  const a = new Date(s.check_in).getTime(), b = new Date(s.check_out).getTime()
  return Number.isFinite(a) && Number.isFinite(b) ? Math.max(0, Math.round((b - a) / 86_400_000)) : 0
}

export const persoane = (s: StareWizard) => nr(s.adults) + nr(s.children) + nr(s.infants)

export const turistiiTuturorCamerelor = (s: StareWizard) => s.rooms.flatMap((c) => c.tourists)

/** Calculul fiscal al rezervarii. `tvaProcent`/`eMarja` vin din nomenclatorul de TVA,
 *  ca sa nu existe o a doua lista de cote in cod. */
export function calculFiscal(s: StareWizard, tvaProcent: number, eMarja: boolean) {
  const cost = nr(s.cost_price)
  const vanzare = nr(s.sale_price)
  const extraVanzare = s.extra_services.reduce((t, x) => t + nr(x.sale_price), 0)
  const extraCost = s.extra_services.reduce((t, x) => t + nr(x.cost_price), 0)
  const totalVanzare = vanzare + extraVanzare
  const totalCost = cost + extraCost
  const marja = totalVanzare - totalCost
  const p = tvaProcent / 100
  // Regim marja: TVA-ul e in marja. Regim normal: TVA peste pretul de vanzare.
  const tva = eMarja
    ? (p > 0 ? marja - marja / (1 + p) : 0)
    : totalVanzare * p
  return {
    cost: totalCost, vanzare: totalVanzare, marja, tva,
    procentMarja: totalVanzare ? (marja / totalVanzare) * 100 : 0,
  }
}

/** Schita se tine in localStorage: serverul NU are inca persistenta de schite
 *  (`/wizard/draft/save` raspunde „placeholder"), iar munca de 10 minute nu are voie
 *  sa dispara la un refresh sau la o intrerupere. */
const CHEIE = 'et.wizard.rezervare'

export const salveazaSchita = (s: StareWizard) => {
  try { localStorage.setItem(CHEIE, JSON.stringify({ salvat: Date.now(), stare: s })) } catch { /* plin */ }
}

export const citesteSchita = (): { salvat: number; stare: StareWizard } | null => {
  try {
    const v = localStorage.getItem(CHEIE)
    if (!v) return null
    const d = JSON.parse(v) as { salvat: number; stare: StareWizard }
    return d?.stare ? d : null
  } catch { return null }
}

export const stergeSchita = () => { try { localStorage.removeItem(CHEIE) } catch { /* nimic */ } }

/** Camerele se genereaza din numarul de camere si persoane, ca sa nu le bata omul de mana. */
export function genereazaCamere(s: StareWizard): Camera[] {
  const camere = Math.max(1, nr(s.num_rooms))
  const adulti = nr(s.adults), copii = nr(s.children)
  const out: Camera[] = []
  for (let i = 0; i < camere; i++) {
    const aici = Math.floor(adulti / camere) + (i < adulti % camere ? 1 : 0)
    const copiiAici = Math.floor(copii / camere) + (i < copii % camere ? 1 : 0)
    const tourists: Turist[] = []
    for (let j = 0; j < aici; j++) tourists.push({ full_name: '', is_titular: j === 0 })
    for (let j = 0; j < copiiAici; j++) tourists.push({ full_name: '' })
    out.push({ room_type: aici >= 3 ? 'TPL' : aici === 1 ? 'SGL' : 'DBL', meal_type: 'BB', tourists })
  }
  return out
}

/** Un grup de facturare pe fiecare camera — asa se factureaza in practica sejururile.
 *  Sumele se impart egal si raman editabile. */
export function genereazaGrupuri(s: StareWizard, totalVanzare: number): GrupFacturare[] {
  const camere = s.rooms.length || 1
  const perCamera = totalVanzare / camere
  return s.rooms.map((c, i) => {
    const titular = c.tourists.find((t) => t.is_titular) ?? c.tourists[0]
    return {
      name: `Camera ${i + 1}`,
      partner_name: titular?.full_name || s.titular_name || `Camera ${i + 1}`,
      partner_vat_no: titular?.cnp ?? null,
      invoice_amount: perCamera.toFixed(2),
      currency: s.currency,
      room_indices: [i],
    }
  })
}
